import { ProductLabelsContent } from "/product.labels";
import { CartDrawerBlocksRoute_protection_product } from "_types";
import { cart } from "cart";
import clsx from "clsx";
import { _Product, useCartDrawer, useGlobalProducts, useGlobalSettings } from "global-stores";
import { Image } from "image";
import { render } from "preact";
import { useProductQuickView } from "product-quick-view";
import { ProductRatingWithProduct } from "product.rating";
import { FC, HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useWindowSize } from "scrollbar";
import { JSONParse, toKebabCase, unescape } from "utils";
import { Typography } from "./typography";

export const initProductCards = async () => {
  const routeProtectionProducts: CartDrawerBlocksRoute_protection_product["settings"][] = [];
  document.querySelectorAll<HTMLElement>("[data-route-protection]").forEach((element) => {
    const settings = JSONParse<CartDrawerBlocksRoute_protection_product["settings"]>(
      element.dataset.routeProtectionSettings
    );
    routeProtectionProducts.push(settings);
  });

  document
    .querySelectorAll<HTMLElement>("[data-product-card]:not([data-product-card-initiated])")
    .forEach(async (productCardContainer) => {
      productCardContainer.setAttribute("data-product-card-initiated", "");
      const collectionUrl = productCardContainer.dataset.productCardCollectionUrl;
      const productHandle = productCardContainer.dataset.quickViewProduct;
      const filterColor = productCardContainer
        .closest(".shopify-section")
        .querySelector<HTMLElement>(`[data-collection-color-filter-active]`)?.dataset
        .collectionColorFilterActive;

      const isLimitedRouteProtectionProduct = !!routeProtectionProducts.find(
        (block) => block?.products?.[0]?.handle === productHandle
      )?.limit_quantity;

      if (
        !isLimitedRouteProtectionProduct &&
        !productCardContainer.querySelector("[data-product-card-variant-selector]") &&
        !window?.Shopify?.designMode
      ) {
        productCardContainer
          ?.querySelector("[data-product-card-image-overlay]")
          ?.classList?.add("opacity-0", "pointer-events-none");
        return;
      }

      const productData = useGlobalProducts.getState().getProduct(productHandle);

      if (!productData) {
        const subscription = useGlobalProducts.subscribe((state) => {
          const product = state.getProduct(productHandle);

          if (product) {
            subscription();
            productCardContainer.innerHTML = "";

            render(
              <ProductCardInner
                filterColor={filterColor}
                product={product}
                collectionUrl={collectionUrl}
                preload={!!productCardContainer.hasAttribute("data-product-card-preload")}
                productCardContainer={productCardContainer}
              />,
              productCardContainer
            );
          }
        });
      } else {
        productCardContainer.innerHTML = "";

        render(
          <ProductCardInner
            filterColor={filterColor}
            product={productData}
            collectionUrl={collectionUrl}
            productCardContainer={productCardContainer}
          />,
          productCardContainer
        );
      }
    });

  const MutationObserver = window.MutationObserver || window["WebKitMutationObserver"];

  // define a new observer
  const mutationObserver = new MutationObserver((e) => {
    e?.forEach((record) =>
      record.addedNodes.forEach(async (node) => {
        if (node?.dataset?.productCard && !node?.dataset?.productCardInitiated) {
          const productCardContainer = node as HTMLElement;
          productCardContainer.setAttribute("data-product-card-initiated", "");
          const collectionUrl = productCardContainer.dataset.productCardCollectionUrl;
          const productHandle = productCardContainer.dataset.quickViewProduct;
          const filterColor = productCardContainer
            .closest(".shopify-section")
            .querySelector<HTMLElement>(`[data-collection-color-filter-active]`)?.dataset
            .collectionColorFilterActive;

          const isLimitedRouteProtectionProduct = !!routeProtectionProducts.find(
            (block) => block?.products?.[0]?.handle === productHandle
          )?.limit_quantity;

          if (
            !isLimitedRouteProtectionProduct &&
            !productCardContainer.querySelector("[data-product-card-variant-selector]") &&
            !window?.Shopify?.designMode
          ) {
            return;
          }

          const productData = useGlobalProducts.getState().getProduct(productHandle);

          if (!productData) {
            const subscription = useGlobalProducts.subscribe((state) => {
              const product = state.getProduct(productHandle);

              if (product) {
                subscription();
                productCardContainer.innerHTML = "";

                render(
                  <ProductCardInner
                    filterColor={filterColor}
                    product={product}
                    collectionUrl={collectionUrl}
                    preload={!!productCardContainer.hasAttribute("data-product-card-preload")}
                    productCardContainer={productCardContainer}
                  />,
                  productCardContainer
                );
              }
            });
          } else {
            productCardContainer.innerHTML = "";

            render(
              <ProductCardInner
                filterColor={filterColor}
                product={productData}
                collectionUrl={collectionUrl}
                productCardContainer={productCardContainer}
              />,
              productCardContainer
            );
          }
        }
      })
    );
  });

  // have the observer observe foo for changes in children
  mutationObserver.observe(document.body, { childList: true, subtree: true });
};

export const ProductCardInner: FC<
  {
    product: _Product;
    collectionUrl?: string;
    className?: string;
    filterColor?: string;
    productCardContainer: HTMLElement;
    preload?: boolean;
  } & Omit<HTMLAttributes<HTMLElement>, "preload">
> = ({
  product,
  collectionUrl,
  className,
  productCardContainer,
  filterColor,
  preload,
  ...props
}) => {
  const { product_data__preorder_cta } = useGlobalSettings();
  const containerRef = useRef<HTMLAnchorElement>(null);
  const { width } = useWindowSize();
  const [containerWidth, setContainerWidth] = useState(400);
  const settings = useGlobalSettings();

  const { product_data__preorder_metafield, product_data__preorder_date_metafield } =
    useGlobalSettings(
      ({ product_data__preorder_metafield, product_data__preorder_date_metafield }) => ({
        product_data__preorder_metafield,
        product_data__preorder_date_metafield,
      })
    );

  const [preorder_ns, preorder_key] = product_data__preorder_metafield.split(".");
  const [date_ns, date_key] = product_data__preorder_date_metafield.split(".");

  const colorOption = useMemo(() => {
    let options = product?.options_with_values?.find(
      (option) =>
        settings?.product_card__variant_selector__color_list
          .split(",")
          .map((c) => c.trim().toLowerCase())
          .includes(option.name.trim().toLowerCase())
    );

    const availableValues = options?.values?.filter((value) => {
      const index = options.position - 1;
      const notAvailable = !product?.variants?.some(
        (variant) => variant.options[index] === value && variant.available
      );

      return !(settings.product_data__hide_oos_variants && notAvailable);
    });

    const value = settings.collection__filter_variant_select
      ? product?.variants?.find((v) => {
          if (v?.options?.[options?.position - 1]?.toLowerCase() === filterColor?.toLowerCase()) {
            return true;
          }
          const [namespace, key] = (
            settings?.collection__filter_variant_color_metafield ?? ""
          ).split(".");

          if (namespace && key) {
            return (
              ((v.metafields?.[namespace]?.[key] ?? "") as string)?.toLowerCase() ===
              filterColor?.toLowerCase()
            );
          }
          return false;
        })?.options?.[options?.position - 1] ??
        availableValues?.[0] ??
        options?.values?.[0]
      : availableValues?.[0] ?? options?.values?.[0];

    options = {
      ...options,
      values: [
        options?.values.find((val) => val === value),
        ...(options?.values?.filter((val) => val !== value) ?? []),
      ],
    };
    const mediaIds = [];

    const variantWithPrimaryImage = product?.variants?.find((v) => {
      if (
        v.options[options?.position - 1] === value &&
        v.featured_media &&
        !mediaIds.includes(v.featured_media?.id)
      ) {
        mediaIds.push(v.featured_media?.id);
        return true;
      }
      return false;
    });

    const variantWithSecondaryImage = product?.variants?.find((v) => {
      if (
        v.options[options?.position - 1] === value &&
        v.featured_media &&
        !mediaIds.includes(v.featured_media?.id)
      ) {
        mediaIds.push(v.featured_media?.id);
        return true;
      }
      return false;
    });

    return {
      ...options,
      values: options?.values?.map((value) => {
        const index = options.position - 1;
        const notAvailable = !product?.variants?.some(
          (variant) => variant.options[index] === value && variant.available
        );

        // return !(settings.product_data__hide_oos_variants && notAvailable);
        return {
          value,
          available: !notAvailable,
        };
      }),
      value: value,
      variantWithPrimaryImage,
      variantWithSecondaryImage,
    };
  }, [
    filterColor,
    product?.options_with_values,
    product.variants,
    settings?.collection__filter_variant_color_metafield,
    settings.collection__filter_variant_select,
    settings?.product_card__variant_selector__color_list,
    settings.product_data__hide_oos_variants,
  ]);

  const { cartData, routeProtectionProducts } = useCartDrawer(
    ({ cartData, routeProtectionProducts }) => ({ cartData, routeProtectionProducts })
  );

  const inCartItem = cartData?.items?.find(
    (line_item) => product.variants?.some((variant) => line_item.variant_id === variant.id)
  );
  const routeProtection = inCartItem
    ? routeProtectionProducts.find((block) => block?.products?.[0]?.handle === inCartItem?.handle)
    : null;

  const isSingleQuantityRouteProtectionItem = !!routeProtection?.limit_quantity;

  const [selectedColor, setSelectedColor] = useState(colorOption?.value);

  const [primaryImage, setPrimaryImage] = useState(
    settings.product_card__image__default_source === "variant_image"
      ? colorOption?.variantWithPrimaryImage?.featured_media?.preview_image ??
          product?.featured_media?.preview_image
      : product?.featured_media?.preview_image
  );
  const [secondaryImage, setSecondaryImage] = useState(() => {
    const value = selectedColor;
    const mediaIds = [];

    const variantWithPrimaryImage = product?.variants?.find((v) => {
      if (
        v.options[colorOption?.position - 1] === value &&
        v.featured_media &&
        !mediaIds.includes(v.featured_media?.id)
      ) {
        mediaIds.push(v.featured_media?.id);
        return true;
      }
      return false;
    });

    if (
      variantWithPrimaryImage &&
      settings.product_card__image__default_source === "variant_image"
    ) {
      setPrimaryImage(
        variantWithPrimaryImage.featured_media?.preview_image ??
          product?.featured_media?.preview_image
      );
    }

    switch (settings.product_card__image__secondary_source) {
      case "product": {
        return product?.media?.[1]?.preview_image;
      }
      case "variant": {
        const nextImagePosition = product.media.find(
          (m) =>
            m.position > variantWithPrimaryImage.featured_media.position &&
            product.variants?.some((v) => v.featured_media?.id === m.id)
        )?.position;

        const availableImage =
          product.media
            .slice(
              variantWithPrimaryImage.featured_media.position,
              nextImagePosition ? nextImagePosition - 1 : nextImagePosition
            )
            ?.at(1) ?? variantWithPrimaryImage?.featured_media;
        return availableImage;
      }
      case "metafield": {
        const [namespace, key] = settings.product_card__image__secondary_source_metafield
          .trim()
          .split(".");
        const image = Array.isArray(variantWithPrimaryImage?.metafields?.[namespace]?.[key])
          ? variantWithPrimaryImage?.metafields?.[namespace]?.[key]?.[0]
          : variantWithPrimaryImage?.metafields?.[namespace]?.[key];

        return image
          ? { aspect_ratio: 1, height: 500, src: image, width: 500 }
          : variantWithPrimaryImage?.featured_media;
      }
    }
  });

  const selectedVariant = colorOption?.value
    ? product.variants.find((v) => v.option1 === selectedColor)
    : product?.variants?.[0];

  const handleClick = useCallback(
    async (preview = false) => {
      if (isSingleQuantityRouteProtectionItem) {
        return;
      }

      if (
        ((product.variants.length > 1 ||
          !settings.product_card__button_single_variant_add_to_cart) &&
          !settings.product_card__button_color_variant_add_to_cart) ||
        product.options.filter(Boolean).length > 1 ||
        preview
      ) {
        const disableCartOpen = productCardContainer?.dataset?.productCardNoAutoCart;
        useProductQuickView.getState().setProduct(product, !disableCartOpen);
        setTimeout(() => document.dispatchEvent(new Event("product-quick-view:open")), 1);
      }
      if (
        !preview &&
        ((product.variants.length <= 1 &&
          settings.product_card__button_single_variant_add_to_cart) ||
          (product.options.filter(Boolean).length <= 1 &&
            settings.product_card__button_color_variant_add_to_cart))
      ) {
        const pre_order =
          selectedVariant?.metafields?.[preorder_ns]?.[preorder_key] ??
          product?.metafields?.[preorder_ns]?.[preorder_key];

        const pre_order_date =
          selectedVariant?.metafields?.[date_ns]?.[date_key] ??
          product?.metafields?.[date_ns]?.[date_key];

        if (pre_order && selectedVariant?.inventory_quantity < 1) {
          const now_date = Date.now();
          let label = "On Preorder";
          let timeOfMonth = "early ";

          if (pre_order_date) {
            const target_date = new Date(pre_order_date as string).getTime();
            const target_day = +new Date(pre_order_date as string).toLocaleString("en", {
              day: "2-digit",
            });
            const target_month = new Date(pre_order_date as string).toLocaleString("en", {
              month: "long",
            });
            const difference = Math.round((target_date - now_date) / 1000 / 60 / 60 / 24);

            if (target_day > 7) {
              timeOfMonth = "mid ";
            }
            if (target_day > 20) {
              timeOfMonth = "end of ";
            }

            label = "Preorder Shipping this week";

            if (difference > 7) {
              label = "Preorder Shipping next week";
            }
            if (difference > 15) {
              label = "Preorder Shipping end of the month";
            }
            if (difference > 31) {
              label = "Preorder Shipping early next month";
            }
            if (difference > 45) {
              label = `Preorder Shipping ${timeOfMonth} ${target_month}`;
            }
          }

          if (selectedVariant?.inventory_quantity > 0) {
            await cart.add(
              {
                items: [
                  {
                    id: selectedVariant?.id,
                    quantity: selectedVariant?.inventory_quantity,
                    selling_plan: product?.requires_selling_plan
                      ? product?.selling_plan_groups?.[0]?.selling_plans?.[0]?.id
                      : undefined,
                  },
                  {
                    id: selectedVariant?.id,
                    quantity: 1 - selectedVariant?.inventory_quantity,
                    properties: {
                      preorder: label,
                    },
                    selling_plan: product?.requires_selling_plan
                      ? product?.selling_plan_groups?.[0]?.selling_plans?.[0]?.id
                      : undefined,
                  },
                ],
              },
              true
            );
          }

          if (selectedVariant?.inventory_quantity <= 0) {
            await cart.add(
              {
                items: [
                  {
                    id: selectedVariant?.id,
                    quantity: 1,
                    properties: {
                      preorder: label,
                    },
                    selling_plan: product?.requires_selling_plan
                      ? product?.selling_plan_groups?.[0]?.selling_plans?.[0]?.id
                      : undefined,
                  },
                ],
              },
              true
            );
          }
          return;
        }

        if (!pre_order || selectedVariant?.inventory_quantity >= 1) {
          await cart.add(
            {
              items: [
                {
                  id: selectedVariant?.id,
                  quantity: 1,
                  selling_plan: product?.requires_selling_plan
                    ? product?.selling_plan_groups?.[0]?.selling_plans?.[0]?.id
                    : undefined,
                },
              ],
            },
            true
          );
          return;
        }

        await cart.add(
          {
            items: [
              {
                id: product?.variants?.[0].id,
                quantity: 1,
                selling_plan: product?.requires_selling_plan
                  ? product?.selling_plan_groups?.[0]?.selling_plans?.[0]?.id
                  : undefined,
              },
            ],
          },
          true
        );
      }
    },
    [
      isSingleQuantityRouteProtectionItem,
      product,
      settings.product_card__button_single_variant_add_to_cart,
      settings.product_card__button_color_variant_add_to_cart,
      productCardContainer?.dataset?.productCardNoAutoCart,
      selectedVariant?.metafields,
      selectedVariant?.inventory_quantity,
      selectedVariant?.id,
      preorder_ns,
      preorder_key,
      date_ns,
      date_key,
    ]
  );

  const handleColorSelect = useCallback(
    (value) => {
      setSelectedColor(value);
      const mediaIds = [];

      const variantWithPrimaryImage = product.variants.find((v) => {
        if (
          v.options[colorOption?.position - 1] === value &&
          v.featured_media &&
          !mediaIds.includes(v.featured_media?.id)
        ) {
          mediaIds.push(v.featured_media?.id);
          return true;
        }
        return false;
      });

      if (variantWithPrimaryImage) {
        setPrimaryImage(
          variantWithPrimaryImage.featured_media?.preview_image ??
            product?.featured_media?.preview_image
        );
      }
      switch (settings.product_card__image__secondary_source) {
        case "product": {
          setSecondaryImage(product?.media?.[1]?.preview_image);
          break;
        }
        case "variant": {
          const nextImagePosition = product.media.find(
            (m) =>
              m.position > variantWithPrimaryImage.featured_media.position &&
              product.variants?.some((v) => v.featured_media?.id === m.id)
          )?.position;
          const availableImage =
            product.media
              .slice(
                variantWithPrimaryImage.featured_media.position,
                nextImagePosition ? nextImagePosition - 1 : nextImagePosition
              )
              ?.at(1) ?? variantWithPrimaryImage?.featured_media;
          setSecondaryImage(availableImage);
          break;
        }
        case "metafield": {
          const [namespace, key] = settings.product_card__image__secondary_source_metafield
            .trim()
            .split(".");
          const image = Array.isArray(variantWithPrimaryImage?.metafields?.[namespace]?.[key])
            ? variantWithPrimaryImage?.metafields?.[namespace]?.[key]?.[0]
            : variantWithPrimaryImage?.metafields?.[namespace]?.[key];

          setSecondaryImage(
            image
              ? { aspect_ratio: 1, height: 500, src: image, width: 500 }
              : variantWithPrimaryImage?.featured_media
          );

          break;
        }
      }
      // eslint-disable-next-line max-len
    },
    [
      colorOption?.position,
      settings.product_card__image__secondary_source,
      product,
      settings.product_card__image__secondary_source_metafield,
    ]
  );

  const handlePreloadImage = useCallback(
    (value) => {
      const mediaIds = [];

      const variantWithPrimaryImage = product.variants.find((v) => {
        if (
          v.options[colorOption?.position - 1] === value &&
          v.featured_media &&
          !mediaIds.includes(v.featured_media?.id)
        ) {
          mediaIds.push(v.featured_media?.id);
          return true;
        }
        return false;
      });

      const variantWithSecondaryImage = product.variants.find((v) => {
        if (
          v.options[colorOption?.position - 1] === value &&
          v.featured_media &&
          !mediaIds.includes(v.featured_media?.id)
        ) {
          mediaIds.push(v.featured_media?.id);
          return true;
        }
        return false;
      });

      if (variantWithPrimaryImage) {
        const imgEl = document.createElement("img");
        const src = variantWithPrimaryImage?.featured_media.preview_image.src
          .replace(/&v=\d+(&)/gi, "$1")
          .replace(/\?v=\d+(&)/gi, "?")
          .replace(/\?v=\d+$/gi, "");

        imgEl.src = `${src}&width=640`;
        imgEl.onload = () => imgEl.remove();
      }
      if (variantWithSecondaryImage) {
        const imgEl = document.createElement("img");

        const src = variantWithSecondaryImage?.featured_media.preview_image.src
          .replace(/&v=\d+(&)/gi, "$1")
          .replace(/\?v=\d+(&)/gi, "?")
          .replace(/\?v=\d+$/gi, "");
        imgEl.src = `${src}&width=640`;
        imgEl.onload = () => imgEl.remove();
      }
    },
    [colorOption?.position, product.variants]
  );

  const getColorSwatch = useCallback(
    (value: string) => {
      const variantWithImage = product.variants.find(
        (variant) =>
          variant.options[colorOption.position - 1] === value && variant.featured_media?.src
      );

      const [namespace, key] = settings.product_data__variants_color_swatches.trim().split(".");

      const variantWithMetafield = product.variants.find(
        (variant) =>
          variant.options[colorOption.position - 1] === value &&
          variant?.metafields?.[namespace]?.[key]
      );

      switch (settings.product_card__color_selector) {
        case "via_option": {
          return `var(--color-swatch--${toKebabCase(value)}, ${value})`;
        }
        case "via_option_then_image": {
          if (variantWithImage) {
            return `var(--color-swatch--${toKebabCase(value)}, url("${variantWithImage
              ?.featured_media?.src}&width=${settings.product_card__color_selector_size}&height=${
              settings.product_card__color_selector_size
            }"))`;
          }
          return `var(--color-swatch--${toKebabCase(value)}, ${value})`;
        }
        case "via_image": {
          if (variantWithImage) {
            return `url(${variantWithImage?.featured_media?.src}&width=${settings.product_card__color_selector_size}&height=${settings.product_card__color_selector_size})`;
          }
          return `var(--color-swatch--${toKebabCase(value)}, ${value})`;
        }
        case "via_metafield": {
          if (variantWithMetafield) {
            return `${variantWithMetafield?.metafields?.[namespace]?.[key]}`;
          }
          return `var(--color-swatch--${toKebabCase(value)}, ${value})`;
        }
        case "via_image_then_metafield": {
          if (variantWithImage) {
            return `url(${variantWithImage?.featured_media?.src}&width=${settings.product_card__color_selector_size}&height=${settings.product_card__color_selector_size})`;
          }
          if (variantWithMetafield) {
            return `${variantWithMetafield?.metafields?.[namespace]?.[key]}`;
          }
          return `var(--color-swatch--${toKebabCase(value)}, ${value})`;
        }
        case "via_metafield_then_image": {
          if (variantWithMetafield) {
            return `${variantWithMetafield?.metafields?.[namespace]?.[key]}`;
          }
          if (variantWithImage) {
            return `url(${variantWithImage?.featured_media?.src}&width=${settings.product_card__color_selector_size}&height=${settings.product_card__color_selector_size})`;
          }
          return `var(--color-swatch--${toKebabCase(value)}, ${value})`;
        }
      }
    },
    [
      colorOption.position,
      product.variants,
      settings.product_card__color_selector,
      settings.product_card__color_selector_size,
      settings.product_data__variants_color_swatches,
    ]
  );

  useEffect(() => {
    if (containerRef.current?.clientWidth) {
      setContainerWidth(containerRef.current?.clientWidth);
    }
  }, [width]);

  useEffect(() => {
    if (!primaryImage && product?.featured_media?.preview_image) {
      setPrimaryImage(product?.featured_media?.preview_image);
    }
  }, [primaryImage, product?.featured_media?.preview_image]);

  return (
    <>
      <a
        ref={containerRef}
        className={clsx(
          "product-card__image group relative grid h-0 outline-none hf:outline-none ",
          settings.product_card__image__ratio
        )}
        href={collectionUrl ? collectionUrl + product.url : product.url}
        aria-label={product.title}
        style={{ marginBottom: `${settings.product_card__image__margin_bottom}px` }}
      >
        <div
          className={clsx("absolute inset-0 h-full w-full overflow-hidden")}
          style={{ background: settings.product_card__image__background }}
        >
          <Image
            src={primaryImage?.src}
            loading={preload ? "eager" : undefined}
            width={640}
            height={primaryImage?.aspect_ratio * 640}
            maxWidth={640}
            alt={product?.featured_media?.alt ?? product.title}
            sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
            className={clsx(
              "inset-0 h-full w-full select-none object-cover",
              settings.product_card__image__drop_shadow && "drop-shadow-product"
            )}
          />
        </div>
        {settings.product_card__image__show_secondary && secondaryImage?.src
          ? <div
              className="absolute inset-0 h-full w-full overflow-hidden opacity-0 transition-all duration-75 hf:opacity-100"
              style={{ background: settings.product_card__background_color as string }}
            >
              <div
                className="absolute inset-0"
                style={{ background: settings.product_card__image__background }}
              ></div>
              <Image
                src={secondaryImage?.src}
                loading={preload ? "eager" : undefined}
                width={640}
                height={secondaryImage?.aspect_ratio * 640}
                maxWidth={640}
                alt={product.title}
                sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                className={clsx(
                  "relative inset-0 h-full w-full select-none object-cover",
                  settings.product_card__image__drop_shadow && "drop-shadow-product"
                )}
              />
            </div>
          : null}
        {settings.product_card__variant_count__show && product?.variants?.length > 1
          ? <div
              className={clsx(
                "pointer-events-none absolute bottom-2 left-1/2 w-[calc(100%-16px)] -translate-x-1/2 rounded-sm bg-white/80 px-2 py-2 text-center text-[11px] font-semibold transition-all duration-75 md:bottom-3 md:w-[calc(100%-24px)] md:text-xs",
                settings.product_card__variant_count__show_hover &&
                  "opacity-0 group-hf:opacity-100",
                settings.product_card__variant_count__responsive_visibility
              )}
            >
              Available in {product?.variants?.length} variations
            </div>
          : null}

        {settings.product_card__labels__show
          ? <div
              className={clsx(
                "absolute right-3 top-3 -m-0.5 flex max-w-[80%] flex-wrap justify-end gap-1",
                settings.product_card__labels__responsive_visibility
              )}
            >
              <ProductLabelsContent product={product} selectedVariant={selectedVariant} />
            </div>
          : null}
      </a>
      <div
        className={clsx(
          "product-card__title relative flex flex-1 flex-col items-stretch gap-1 p-0 md:h-min",
          settings.product_card__title__align_horizontal
        )}
      >
        <div style={{ marginBottom: `${settings.product_card__title__margin_bottom}px` }}>
          <a
            className="product-card-title outline-none hf:underline hf:outline-none"
            href={collectionUrl ? collectionUrl + product.url : product.url}
          >
            {" "}
            {product.title}
          </a>
        </div>

        {settings.product_card__rating__show
          ? <ProductRatingWithProduct
              product={product}
              className={clsx(
                settings.product_card__rating__responsive_visibility,
                "product-card__reviews"
              )}
              style={{ marginBottom: `${settings.product_card__rating__margin_bottom}px` }}
            />
          : null}

        {settings.product_card__vendor__show && product.vendor
          ? <h2
              className={clsx(
                "product-card__vendor inline-flex text-sm text-theme-text/60",
                settings.product_card__vendor__responsive_visibility
              )}
              style={{ marginBottom: `${settings.product_card__vendor__margin_bottom}px` }}
            >
              <a
                href={`/collections/vendors?q=${encodeURIComponent(product.vendor)}`}
                className="hf:underline"
                title={product.vendor}
              >
                {product.vendor}
              </a>
            </h2>
          : null}

        {settings.product_card__description__show && product?.metafields?.smart?.short_description
          ? <div className="product-card__description text-balance px-2">
              <Typography fontType={2}>
                {unescape((product?.metafields?.smart?.short_description as string) ?? "")}
              </Typography>
            </div>
          : null}

        {settings.product_card__price__show
          ? <div
              className={clsx(
                "product-card__price",
                settings.product_card__price__responsive_visibility
              )}
              style={{ marginBottom: `${settings.product_card__price__margin_bottom}px` }}
            >
              <div className="product-price grid auto-cols-min grid-flow-col-dense items-baseline gap-2 whitespace-nowrap">
                <span
                  data-product-price-from-regular=""
                  className={clsx(
                    "text-xs font-normal",
                    product.price_varies && !product.compare_at_price_varies ? "" : "hidden"
                  )}
                >
                  From:
                </span>

                <span
                  data-product-price-from-compare=""
                  className={clsx(
                    "text-xs font-normal",
                    product.compare_at_price_varies ? "" : "hidden"
                  )}
                >
                  On Sale from:
                </span>

                <span
                  data-product-price-regular=""
                  dangerouslySetInnerHTML={{
                    __html: `${window.formatMoney(product.price_min)}`,
                  }}
                ></span>

                <span
                  data-product-price-compare=""
                  className={clsx(
                    "text-xs line-through",
                    product.compare_at_price_min > product.price_min ? "" : "hidden"
                  )}
                  dangerouslySetInnerHTML={{
                    __html: `${window.formatMoney(product.compare_at_price_min)}`,
                  }}
                ></span>
              </div>
            </div>
          : null}
        <div className="product-card__description text-balance px-2">
          <Typography fontType={2}>
            {unescape((product?.metafields?.smart?.short_description as string) ?? "")}
          </Typography>
        </div>

        {settings.product_card__variant_selector__show && colorOption?.values?.length > 1
          ? <div className="product-card__variant-selector relative">
              <fieldset className="group mt-2 flex gap-1">
                {colorOption?.values
                  ?.slice(
                    0,
                    Math.floor(
                      (containerWidth - 70) / (settings.product_card__color_selector_size + 10)
                    ) - 1
                  )
                  .map(({ value, available }) => {
                    return (
                      <label
                        key={value}
                        className={clsx(
                          "flex cursor-pointer items-center justify-center border p-[2px]",
                          value === selectedColor
                            ? "border-black"
                            : "border-[#DEDEDE] hf:border-[#b3b3b3]",
                          !available && "variant-not-available"
                        )}
                        style={{
                          borderRadius:
                            settings.product_card__color_selector_radius >= 0
                              ? `${settings.product_card__color_selector_radius}px`
                              : `9999px`,
                        }}
                        data-tooltip={available ? value : `Sold out: ${value}`}
                        title={value}
                      >
                        <input
                          type="radio"
                          name={`variant-selector-${product.id}-${colorOption?.name}`}
                          className="peer absolute !block appearance-none outline-none hfa:outline-none"
                          value={value}
                          onChange={() => handleColorSelect(value)}
                          onMouseOver={() => handlePreloadImage(value)}
                          onPointerOver={() => handlePreloadImage(value)}
                          checked={value === selectedColor}
                        />

                        <span
                          className="flex h-[22px] w-[22px] items-center justify-center overflow-hidden rounded-full !bg-contain !bg-center peer-disabled:cursor-not-allowed peer-disabled:opacity-60 svg:opacity-0 peer-checked:svg:opacity-100 peer-disabled:hf:opacity-70"
                          style={{
                            borderRadius:
                              settings.product_card__color_selector_radius >= 0
                                ? `${settings.product_card__color_selector_radius}px`
                                : `9999px`,
                            width: `${settings.product_card__color_selector_size}px`,
                            height: `${settings.product_card__color_selector_size}px`,
                            background: getColorSwatch(value),
                          }}
                        ></span>
                      </label>
                    );
                  })}
                {colorOption?.values?.length >
                Math.floor(
                  (containerWidth - 70) / (settings.product_card__color_selector_size + 10)
                ) -
                  1
                  ? <>
                      {/*<a
                        className="cursor-pointer rounded-full border border-theme-text/50"
                        style={{ display: "var(--product-quick-view-off-display, flex)" }}
                        href={collectionUrl ? collectionUrl + product.url : product.url}
                      >
                        <span className="sr-only">View More</span>
                        <span className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-primary-bg/10 ring-2 ring-transparent ring-offset-1 hf:bg-primary-bg/20 md:h-8 md:w-8">
                          <PlusIcon className="h-3 w-3 text-theme-text/70" />
                        </span>
                      </a>*/}
                      <button
                        className="h-7 cursor-pointer items-center gap-1 whitespace-nowrap text-sm"
                        type="button"
                        title="View More"
                        data-product-quick-view-add-button={product.handle}
                        onClick={() => handleClick(true)}
                        style={{ display: "var(--product-quick-view-on-display, flex)" }}
                      >
                        <span>
                          +
                          {colorOption?.values?.length -
                            (Math.floor(
                              (containerWidth - 70) /
                                (settings.product_card__color_selector_size + 10)
                            ) -
                              1)}{" "}
                          Colors
                        </span>
                      </button>
                    </>
                  : null}
              </fieldset>
            </div>
          : null}

        {/* <div
          className={clsx(
            "mt-auto w-full flex-1 flex-col justify-end self-end text-center",
            productCardContainer?.getAttribute("data-product-card-hide-button") ? "hidden" : "flex"
          )}
        >
          {product.available
            ? ((product.variants.length > 1 ||
                !settings.product_card__button_single_variant_add_to_cart) &&
                !settings.product_card__button_color_variant_add_to_cart) ||
              product.options.filter(Boolean).length > 1
              ? <>
                  <a
                    className={clsx(
                      "mt-2 w-full",
                      settings.product_card__button__style,
                      settings.product_card__button__size
                    )}
                    style={{ display: "var(--product-quick-view-off-display, flex)" }}
                    href={collectionUrl ? collectionUrl + product.url : product.url}
                  >
                    <span>
                      <span className="max-md:hidden">
                        {settings.product_card__button_options_text__desktop}
                      </span>
                      <span className="md:hidden">
                        {settings.product_card__button_options_text__mobile}
                      </span>
                    </span>
                    {settings.product_card__button_show_price
                      ? <span
                          dangerouslySetInnerHTML={{
                            __html: `${window.formatMoney(product.price_min)}`,
                          }}
                        />
                      : null}
                  </a>
                  <button
                    className={clsx(
                      "mt-2 w-full",
                      settings.product_card__button__style,
                      settings.product_card__button__size
                    )}
                    // data-product-quick-view-add-button={product.handle}
                    onClick={() => handleClick()}
                    style={{ display: "var(--product-quick-view-on-display, flex)" }}
                  >
                    <span>
                      <span className="max-md:hidden">
                        {settings.product_card__button_options_text__desktop}
                      </span>
                      <span className="md:hidden">
                        {settings.product_card__button_options_text__mobile}
                      </span>
                    </span>
                    {settings.product_card__button_show_price
                      ? <span
                          dangerouslySetInnerHTML={{
                            __html: `${window.formatMoney(product.price_min)}`,
                          }}
                        />
                      : null}
                  </button>
                </>
              : <button
                  className={clsx(
                    "mt-2 w-full",
                    settings.product_card__button__style,
                    settings.product_card__button__size
                  )}
                  // data-product-quick-view-add-button={product.handle}
                  onClick={() => handleClick()}
                  disabled={!product.variants[0].available || isSingleQuantityRouteProtectionItem}
                >
                  {isSingleQuantityRouteProtectionItem
                    ? <span>Already Added</span>
                    : (product.variants?.[0]?.metafields?.[preorder_ns]?.[preorder_key] ||
                        product?.metafields?.[preorder_ns]?.[preorder_key]) &&
                      product_data__preorder_cta &&
                      product.variants?.[0].inventory_quantity <= 0
                    ? <span>Add on Pre-Order</span>
                    : <span>
                        <span className="max-md:hidden">
                          {settings.product_card__button_add_text__desktop}
                        </span>
                        <span className="md:hidden">
                          {settings.product_card__button_add_text__mobile}
                        </span>
                      </span>}
                  {settings.product_card__button_show_price
                    ? <span
                        dangerouslySetInnerHTML={{
                          __html: `${window.formatMoney(product.price_min)}`,
                        }}
                      />
                    : null}
                </button>
            : <a
                className={clsx(
                  "mt-2 w-full",
                  settings.product_card__button__style,
                  settings.product_card__button__size
                )}
                aria-disabled="true"
              >
                <span>
                  <span className="max-md:hidden">
                    {settings.product_card__button_sold_out_text__desktop}
                  </span>
                  <span className="md:hidden">
                    {settings.product_card__button_sold_out_text__mobile}
                  </span>
                </span>
                {settings.product_card__button_show_price
                  ? <span
                      dangerouslySetInnerHTML={{
                        __html: `${window.formatMoney(product.price_min)}`,
                      }}
                    />
                  : null}
              </a>}
        </div> */}
      </div>
    </>
  );
};

export const ProductCardContainer: FC<
  {
    product?: _Product;
    handle?: string;
    collectionUrl?: string;
    className?: string;
  } & HTMLAttributes<HTMLElement>
> = ({ handle, collectionUrl, className, product, ...props }) => {
  const { getAsyncProduct, hydrated } = useGlobalProducts(({ getAsyncProduct, hydrated }) => ({
    getAsyncProduct,
    hydrated,
  }));
  const [currentProduct, setCurrentProduct] = useState(product);
  const settings = useGlobalSettings();
  const productCardContainer = useRef<HTMLElement>();

  useEffect(() => {
    if (hydrated && !currentProduct) {
      window.useGlobalProducts
        .getState()
        .getAsyncProduct(handle)
        .then((p) => {
          if (p) {
            setCurrentProduct(p);
          }
        });
    }
  }, [hydrated, currentProduct, handle]);

  useEffect(() => {
    if (product.metafields && !currentProduct.metafields) {
      setCurrentProduct(product);
    }
  }, [currentProduct.metafields, product]);

  if (!currentProduct) {
    return null;
  }

  return (
    <article
      ref={productCardContainer}
      className={clsx(
        "product-card relative flex flex-col items-stretch space-y-1 overflow-hidden rounded-theme-sm",
        useGlobalSettings.getState().product_card__use_color_scheme &&
          useGlobalSettings.getState().product_card__color_scheme,
        className
      )}
      style={{ background: settings.product_card__background_color as string }}
      {...props}
    >
      <ProductCardInner
        product={currentProduct}
        collectionUrl={collectionUrl}
        productCardContainer={productCardContainer.current}
      />
    </article>
  );
};
